import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { toast } from "sonner";
import { Search, Sparkles, ShieldCheck, MapPin, Award } from "lucide-react";
import { AFRICAN_COUNTRIES, AFRICAN_CITIES_FALLBACK } from "@/lib/countries";

const SERVICE_LEVELS = [
  { key: "standard", label: "Standard — locate suitable vehicles", price: "USD 50" },
  { key: "verified_procurement", label: "Verified Procurement — locate + independent verification", price: "USD 150" },
  { key: "premium_concierge", label: "Premium Concierge — sourced, verified, negotiated, transferred", price: "USD 500" },
  { key: "corporate_fleet", label: "Corporate Fleet — bulk sourcing for organisations", price: "Custom" },
];

export default function FindMyVehicle() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    make: "", model: "", year_min: 2018, year_max: 2024,
    budget_usd_max: 25000, transmission: "any", fuel: "any",
    mileage_km_max: 120000, country: "", city: "",
    urgency: "normal", service_level: "verified_procurement", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const [activeMatches, setActiveMatches] = useState(null);

  const loadMine = async () => {
    if (!user) return;
    try { const r = await api.get("/sourcing/requests/mine"); setMine(r.data); }
    catch {}
  };
  useEffect(() => { loadMine(); /* eslint-disable-next-line */ }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast("Please sign in to submit a request"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        year_min: Number(form.year_min) || null,
        year_max: Number(form.year_max) || null,
        budget_usd_max: Number(form.budget_usd_max),
        mileage_km_max: Number(form.mileage_km_max) || null,
      };
      const r = await api.post("/sourcing/requests", payload);
      toast.success(`Request ${r.data.request_number} submitted — case ${r.data.case_number} opened`);
      loadMine();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const showMatches = async (req) => {
    try {
      const r = await api.get(`/sourcing/requests/${req.id}/matches`);
      setActiveMatches(r.data);
    } catch (e) { toast.error("Could not load matches"); }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <span className="thom-seal"><Search className="h-3 w-3" /> Find My Vehicle™</span>
            <h1 className="display text-4xl md:text-5xl mt-3">Tell us what you're looking for. We'll find it, verify it, and deliver it.</h1>
            <p className="mt-4 text-muted-foreground max-w-lg">
              Not seeing the vehicle you want in the marketplace? Submit a sourcing request and a THOM procurement specialist will
              locate matching vehicles across dealers, private sellers, fleet disposals, and auctions — each one independently verified before it reaches you.
            </p>

            <div className="mt-6 grid grid-cols-3 gap-3">
              <div className="border border-border p-3"><div className="kicker">1</div><div className="text-sm font-medium mt-1">Submit request</div></div>
              <div className="border border-border p-3"><div className="kicker">2</div><div className="text-sm font-medium mt-1">Specialist searches</div></div>
              <div className="border border-border p-3"><div className="kicker">3</div><div className="text-sm font-medium mt-1">Verified matches</div></div>
            </div>
          </div>

          <form onSubmit={submit} className="border border-border bg-card p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Make</Label><Input placeholder="e.g. Toyota" value={form.make} onChange={(e) => setForm({...form, make: e.target.value})} data-testid="fmv-make" /></div>
              <div><Label>Model</Label><Input placeholder="e.g. Hilux" value={form.model} onChange={(e) => setForm({...form, model: e.target.value})} data-testid="fmv-model" /></div>
              <div><Label>Year (min)</Label><Input type="number" value={form.year_min} onChange={(e) => setForm({...form, year_min: e.target.value})} /></div>
              <div><Label>Year (max)</Label><Input type="number" value={form.year_max} onChange={(e) => setForm({...form, year_max: e.target.value})} /></div>
              <div><Label>Max budget (USD)</Label><Input type="number" value={form.budget_usd_max} onChange={(e) => setForm({...form, budget_usd_max: e.target.value})} data-testid="fmv-budget" /></div>
              <div><Label>Max mileage (km)</Label><Input type="number" value={form.mileage_km_max} onChange={(e) => setForm({...form, mileage_km_max: e.target.value})} /></div>
              <div>
                <Label>Transmission</Label>
                <Select value={form.transmission} onValueChange={(v) => setForm({...form, transmission: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="automatic">Automatic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fuel</Label>
                <Select value={form.fuel} onValueChange={(v) => setForm({...form, fuel: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="petrol">Petrol</SelectItem>
                    <SelectItem value="diesel">Diesel</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                    <SelectItem value="electric">Electric</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Country</Label>
                <Select value={form.country} onValueChange={(v) => setForm({...form, country: v})}>
                  <SelectTrigger data-testid="fmv-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                  <SelectContent>{AFRICAN_COUNTRIES.map(c => <SelectItem key={c.iso} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Preferred city</Label>
                <Select value={form.city} onValueChange={(v) => setForm({...form, city: v})}>
                  <SelectTrigger data-testid="fmv-city"><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent>{AFRICAN_CITIES_FALLBACK.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Urgency</Label>
                <Select value={form.urgency} onValueChange={(v) => setForm({...form, urgency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low — no rush</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High — within 2 weeks</SelectItem>
                    <SelectItem value="urgent">Urgent — within 5 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Service level</Label>
              <Select value={form.service_level} onValueChange={(v) => setForm({...form, service_level: v})}>
                <SelectTrigger data-testid="fmv-service-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SERVICE_LEVELS.map(s => <SelectItem key={s.key} value={s.key}>{s.label} — {s.price}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes / required features</Label>
              <Textarea rows={3} placeholder="4x4, leather seats, tow bar, single owner…" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90" data-testid="fmv-submit">
              {busy ? "Submitting…" : "Submit Sourcing Request"}
            </Button>
            {!user && <p className="text-xs text-muted-foreground text-center"><Link className="text-primary hover:underline" to="/login">Sign in</Link> to submit — it takes 30 seconds.</p>}
          </form>
        </div>

        {mine.length > 0 && (
          <div className="mt-14">
            <h2 className="display text-3xl mb-3">Your sourcing requests</h2>
            <Accordion type="single" collapsible defaultValue={mine[0].id}>
              {mine.map((r) => (
                <AccordionItem key={r.id} value={r.id} className="border border-border bg-card rounded-none">
                  <AccordionTrigger className="px-4 hover:no-underline hover:bg-secondary/40">
                    <div className="flex items-center gap-3 text-left flex-1">
                      <span className="mono text-[11px] px-2 py-1 bg-primary text-primary-foreground rounded-sm">{r.request_number}</span>
                      <div>
                        <div className="font-semibold">{r.make || "Any"} {r.model || ""} · up to ${Number(r.budget_usd_max).toLocaleString()}</div>
                        <div className="text-xs mono text-muted-foreground">{r.case_number} · {r.status} · {r.service_level.replace(/_/g, " ")}</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="text-sm text-muted-foreground">Urgency: <b>{r.urgency}</b> · City: <b>{r.city || "any"}</b></div>
                      <Button size="sm" variant="outline" onClick={() => showMatches(r)} data-testid={`fmv-view-matches-${r.id}`}>
                        <Sparkles className="h-4 w-4 mr-1" /> Show matches
                      </Button>
                    </div>
                    {activeMatches && activeMatches.request.id === r.id && (
                      <div className="mt-3 border-t border-border pt-3">
                        <div className="kicker mb-2">Matched from THOM Verified Pool™ · {activeMatches.total} match(es)</div>
                        {activeMatches.matches.length === 0 ? (
                          <div className="text-sm text-muted-foreground">No verified vehicles in the pool currently match. Our procurement specialist will begin an external market search.</div>
                        ) : (
                          <div className="grid md:grid-cols-2 gap-3">
                            {activeMatches.matches.map((m, idx) => (
                              <Link key={idx} to={`/vehicles/${m.vehicle.id}`} className="border border-border p-3 hover:border-primary transition-colors block">
                                <div className="flex items-baseline justify-between gap-2">
                                  <div className="font-semibold">{m.vehicle.year} {m.vehicle.make} {m.vehicle.model}</div>
                                  <div className="mono text-primary font-semibold">${Number(m.vehicle.price_usd).toLocaleString()}</div>
                                </div>
                                <div className="mt-1 text-xs text-muted-foreground flex items-center gap-3">
                                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {m.vehicle.city}</span>
                                  <span className="flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-primary" /> Verified</span>
                                  {m.vehicle.trust_score != null && <span className="flex items-center gap-1"><Award className="h-3 w-3" /> Trust {m.vehicle.trust_score}</span>}
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1">
                                  <span className="mono text-[10px] px-2 py-0.5 bg-primary text-primary-foreground rounded-sm">MATCH {m.score}</span>
                                  {m.reasons.slice(0, 3).map((rr, i) => <span key={i} className="text-[10px] mono px-2 py-0.5 border border-border">{rr}</span>)}
                                </div>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
